<template>
  <v-dialog
    transition="dialog-bottom-transition"
    persistent
    lazy
    scrollable
    :value="show"
    :fullscreen="$vuetify.breakpoint.xsOnly"
    :width="480"
    @input="(val) => $emit('update:show', val)"
  >
    <v-card>
      <v-card-title class="pb-1">
        <v-layout align-center>
          <h1 class="ml-auto headline">
            {{ $t('openinghoursEdit') }}
          </h1>
          <v-btn
            class="ml-auto mr-0 my-0"
            flat
            icon
            :title="$t('close')"
            @click="onAborted()"
          >
            <v-icon>close</v-icon>
          </v-btn>
        </v-layout>
      </v-card-title>
      <v-card-text class="pt-1">
        <v-layout
          column
          align-center
        >
          <span class="mb-3">
            {{ editMultipleHint }}
          </span>
          <v-layout
            v-for="weekday in weekdays"
            :key="'weekday_' + weekday.index"
            align-center
          >
            <v-layout align-baseline>
              <div
                class="title grey--text text--darken-3"
                :class="{
                  'mr-3': $vuetify.breakpoint.smAndUp,
                  'mr-2': $vuetify.breakpoint.xs,
                }"
                style="width: 40px"
              >
                {{ weekday.abbreviation }}
              </div>
              <v-text-field
                v-model="weekday.start"
                :label="$t('from')"
                :rules="[rules.hourMinute]"
                placeholder="hh:mm"
                mask="##:##"
                class="opening-hours-text-field mr-3"
                @keyup.native.enter="onConfirmed()"
              />
              <v-text-field
                v-model="weekday.end"
                :label="$t('to').toLowerCase()"
                :rules="[rules.hourMinute]"
                placeholder="hh:mm"
                mask="##:##"
                class="opening-hours-text-field mr-3"
                @keyup.native.enter="onConfirmed()"
              />
              <v-layout
                align-center
                style="min-width: 28px"
              >
                <!-- Remove -->
                <v-btn
                  v-if="weekday.start || weekday.end"
                  icon
                  small
                  color="grey--text text--darken-1"
                  class="ma-0"
                  :title="$t('remove')"
                  @click="removeOpeningHoursFromWeekday(weekday)"
                >
                  <v-icon small>
                    clear
                  </v-icon>
                </v-btn>
              </v-layout>
            </v-layout>
          </v-layout>
        </v-layout>
      </v-card-text>
      <v-card-actions :class="{'top-border': storeOpeningHoursDiffer}">
        <v-layout column>
          <div
            v-if="storeOpeningHoursDiffer"
            class="mx-2 mb-2 warning--text"
            style="font-weight: bold"
          >
            {{ $t('openinghoursEditMultipleConflictHint') }}
          </div>
          <v-layout justify-center>
            <btn-ok
              round
              :disabled="!formIsValid"
              :loading="loading"
              @click="onConfirmed()"
            />
          </v-layout>
        </v-layout>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import {
  ALL_WEEKDAY_INDICES,
  ALL_WEEKDAY_LOWERCASE_ATTRIBUTES,
  getAllWeekdayNames,
  getAllWeekdayNameAbbreviations,
} from '@/mixins/weekdays';

import BtnOk from '@/components/ButtonOk';

export default {
  components: {
    BtnOk,
  },
  props: {
    show: {
      type: Boolean,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    stores: {
      type: Array[Object],
      required: true,
    },
  },
  data() {
    return {
      weekdays: [],
      storeOpeningHoursDiffer: false,
      rules: {
        hourMinute: (v) => {
          return this.isValidTimestring(v) || 'hh:mm';
        },
      },
    };
  },
  computed: {
    formIsValid() {
      for (let weekday of this.weekdays) {
        if (!this.isValidTimestring(weekday.start)) {
          return false;
        }
        if (!this.isValidTimestring(weekday.end)) {
          return false;
        }
      }
      return true;
    },
    editMultipleHint() {
      if (this.stores.length == 1) {
        return this.stores[0].name + ', ' + this.stores[0].city;
      }
      return this.$t('openinghoursEditMultipleHint').replace(
        '{{ num }}',
        this.stores.length
      );
    },
  },
  watch: {
    stores: function (stores) {
      this.populateInterfaceFromSelectedStores();
    },
  },
  methods: {
    isValidTimestring(text) {
      if (text === '' || text === null) {
        return true;
      }
      // Can be 'hh:mm' or 'hhmm' due to masking.
      if (text.length < 4) {
        return false;
      }
      // Normalize to 'hhmm' form.
      text = text.replace(':', '');
      const firstTwoDigits = text.substr(0, 2);
      const lastTwoDigits = text.substr(2, 2);
      return Number(firstTwoDigits) <= 23 && Number(lastTwoDigits) <= 59;
    },
    removeOpeningHoursFromWeekday(weekday) {
      weekday.start = '';
      weekday.end = '';
    },
    /**
     * Prepare a list of weekday objects we can modify. We always use
     * the first Store to populate the fields, but check if the Store
     * opening hours differ to display a warning that we will lose these
     * differences when confirming the new values.
     */
    populateInterfaceFromSelectedStores() {
      const allWeekdayNames = getAllWeekdayNames();
      const allWeekdayNameAbbreviations = getAllWeekdayNameAbbreviations();

      let firstStore = null;
      if (this.stores && this.stores.length) {
        firstStore = this.stores[0];
      }

      const weekdays = ALL_WEEKDAY_INDICES.map((index) => {
        const weekdayAttribute = ALL_WEEKDAY_LOWERCASE_ATTRIBUTES[index];
        const weekday = {
          index: index,
          name: allWeekdayNames[index],
          abbreviation: allWeekdayNameAbbreviations[index],
          attribute: weekdayAttribute,
          start: null,
          end: null,
        };
        if (firstStore) {
          weekday.start =
            firstStore['opening_hours_' + weekdayAttribute + '_start'];
          weekday.end =
            firstStore['opening_hours_' + weekdayAttribute + '_end'];
        }
        return weekday;
      });

      const allStringifiedOpeningHours = this.stores.map((store) => {
        return (
          '' +
          store.opening_hours_monday_start +
          store.opening_hours_monday_end +
          store.opening_hours_tuesday_start +
          store.opening_hours_tuesday_end +
          store.opening_hours_wednesday_start +
          store.opening_hours_wednesday_end +
          store.opening_hours_thursday_start +
          store.opening_hours_thursday_end +
          store.opening_hours_friday_start +
          store.opening_hours_friday_end +
          store.opening_hours_saturday_start +
          store.opening_hours_saturday_end +
          store.opening_hours_sunday_start +
          store.opening_hours_sunday_end
        );
      });

      this.weekdays = weekdays;
      this.storeOpeningHoursDiffer =
        new Set(allStringifiedOpeningHours).size > 1;
    },
    /**
     * Start/end are saved as 'HHMM' due to the mask of the v-text-fields,
     * but we need to pass it as 'hh:MM' instead. The weekday values
     * are modified here as needed (adding a ':' in between) to
     * accomodate. Values may be correct or wrong, depending whether
     * they have been modified via the masked input or not.
     */
    fixWeekdaysValuesAffectedByMasking(weekdays) {
      for (let weekday of weekdays) {
        if (weekday.start && weekday.start.indexOf(':') === -1) {
          weekday.start =
            weekday.start.substr(0, 2) + ':' + weekday.start.substr(2, 2);
        }
        if (weekday.end && weekday.end.indexOf(':') === -1) {
          weekday.end =
            weekday.end.substr(0, 2) + ':' + weekday.end.substr(2, 2);
        }
      }
    },
    closeDialog() {
      this.$emit('update:show', false);
    },
    onAborted() {
      this.$emit('aborted');
    },
    onConfirmed() {
      this.fixWeekdaysValuesAffectedByMasking(this.weekdays);
      this.$emit('confirmed', this.weekdays);
    },
  },
};
</script>

<style scoped>
.top-border {
  border-top: 1px solid rgba(0, 0, 0, 0.16);
}

.opening-hours-text-field {
  min-width: 100px;
  max-width: 100px;
}
@media (max-width: 599px) {
  .opening-hours-text-field {
    min-width: 48px;
    max-width: 48px;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/EditOpeningHoursDialog.vue